import { ref, computed, watch } from 'vue'
import { useBilling } from '@modules/billing/composables/useBilling'
import useToast from '@src/modules/common/composables/useToast'
import { userMaven } from '@src/tracking/userMavenMixin'

// Global addon adjustments
const addonAdjustments = ref({})

export function useAdjustLimits() {
  // Composables
  const { getExtraCharge, getSubscriptionLimits, updateLimits, getAiTextCreditsPrice } = useBilling()
  const { alertMessage } = useToast()

  // State
  const isAnnually = ref(false)
  const isAgencyPlan = ref(false)
  const originalValues = ref({})
  const hasChanges = ref(false)
  const loading = ref(true)
  const planLimits = ref({})
  const addonsLimits = ref({})
  const usedLimits = ref({})
  const planSlug = ref('')
  const isLoading = ref(false)

  // Values for each add-on
  const minValue = {
    workspaces: 1,
    socialAccounts: 10,
    teamMembers: 0,
    aiImageCredits: 5,
    aiTextCredits: 5000,
    blogs: 0,
    mediaStorage: 10,
  }

  // Initialize max values with subscription limits
  const maxValue = ref({
    workspaces: 1000,
    socialAccounts: 10000,
    teamMembers: 1000,
    aiImageCredits: 10000,
    aiTextCredits: 100000000, // 100M
    blogs: 1000,
    mediaStorage: 1000,
  })

  // Initialize current values with subscription limits
  const currentValue = ref({
    workspaces: 1,
    socialAccounts: 10,
    teamMembers: 3,
    aiImageCredits: 10,
    aiTextCredits: 10000,
    blogs: 1,
    mediaStorage: 1,
  })

  // Prices for each add-on
  const prices = {
    workspaces: 10,
    socialAccounts: 5,
    teamMembers: 10,
    aiImageCredits: 5,
    aiTextCredits: 0.75,
    blogs: 5,
    mediaStorage: 0.5,
  }

  // Limit items configuration
  const limitItems = [
    {
      title: 'Workspaces',
      key: 'workspaces',
      valueKey: 'workspaces',
      priceLabel: '1 workspace',
      agencyOnly: true
    },
    {
      title: 'Team Members',
      key: 'members',
      valueKey: 'teamMembers',
      priceLabel: '1 member',
      agencyOnly: true
    },
    {
      title: 'Social Accounts',
      key: 'profiles',
      valueKey: 'socialAccounts',
      priceLabel: '1 account',
    },
    {
      title: 'Blogs (Publishing)',
      key: 'blogs',
      valueKey: 'blogs',
      priceLabel: '1 blog',
    },
    {
      title: 'Media Storage (GB)',
      key: 'media_storage',
      valueKey: 'mediaStorage',
      priceLabel: '1 GB',
      formatValue: (used, total) => `${formatMediaStorage(used)}/${formatMediaStorage(total)}`,
    },
    {
      title: 'AI Text Credits (per word)',
      key: 'caption_generation_credit',
      valueKey: 'aiTextCredits',
      priceLabel: '1000 words',
      step: 1000,
      formatValue: (used, total) => `${formatNumber(used)}/${formatNumber(total)}`,
    },
    {
      title: 'AI Image Credits',
      key: 'image_generation_credit',
      valueKey: 'aiImageCredits',
      priceLabel: '100 credits',
      step: 100
    },
  ]

  // Filter limit items based on agency plan
  const filteredLimitItems = computed(() => {
    if (isAgencyPlan.value) {
      return limitItems.filter(item => !item.agencyOnly)
    }
    return limitItems
  })
  // Helper functions
  const formatNumber = (number) => {
    if (number >= 1000) {
      return `${(number / 1000).toFixed(0)}K`
    }
    return number.toString()
  }

  const formatMediaStorage = (gb) => {
    return `${gb} GB`
  }

  const formatPrice = (price) => {
    return price.toFixed(2)
  }

  // Calculate price for each item
  const calculateItemPrice = (item) => {
    // Get the base price for the item
    const price = prices[item.valueKey]

    // Calculate the additional units
    const planLimit = planLimits.value[item.valueKey] || minValue[item.valueKey]
    const additionalUnits = currentValue.value[item.valueKey] - planLimit

    // Calculate the price based on the item type
    let itemPrice = 0

    if (additionalUnits > 0) {
      if (item.valueKey === 'socialAccounts') {
        if (isAgencyPlan.value) {
          // For agency plans, use getExtraCharge with total accounts
          const totalAccounts = currentValue.value[item.valueKey]
          itemPrice = getExtraCharge(totalAccounts)
        } else {
          // For non-agency plans, charge per extra account
          itemPrice = additionalUnits * price
        }
      } else if (item.valueKey === 'aiTextCredits') {
        // AI Text Credits are priced per 1000 words
        itemPrice = (additionalUnits / 1000) * price
      } else if (item.valueKey === 'aiImageCredits') {
        // AI Image Credits are priced per 100 credits
        itemPrice = (additionalUnits / 100) * price
      } else {
        itemPrice = additionalUnits * price
      }
    }

    // Apply annual pricing if applicable
    itemPrice = applyDiscount(itemPrice, planSlug.value, isAnnually.value)

    return parseFloat(itemPrice.toFixed(2))
  }

  // Calculate the number of addon units for a specific limit
  const calculateAddonCount = (key) => {
    const planLimit = planLimits.value[key] || minValue[key]
    const additionalUnits = currentValue.value[key] - planLimit
    return Math.max(0, additionalUnits)
  }

  // Methods
  const increaseValue = (key, step = 1) => {
    // No max value restriction
    currentValue.value[key] += step
    updateHasChanges()
  }

  const decreaseValue = (key, step = 1) => {

    // User can only decrease to plan base limits
    if (currentValue.value[key] > usedLimits.value[key]) {
      const baseLimit = planLimits.value[key] || minValue[key]
      let potentialNewValue = Math.max(currentValue.value[key] - step, usedLimits.value[key], baseLimit)
      // Remove base limit from potential new value
      potentialNewValue = potentialNewValue - baseLimit

       // 3. Apply rounding based on the key
    if (key === 'aiTextCredits') {
      potentialNewValue = Math.floor(potentialNewValue / 1000) * 1000;
    } else if (key === 'aiImageCredits') {
      potentialNewValue = Math.floor(potentialNewValue / 100) * 100;
    } else {
      potentialNewValue = Math.round(potentialNewValue);
    }

    // Add base limit back to potential new value
    potentialNewValue = potentialNewValue + baseLimit
    // 5. Update the actual currentValue *only if* it changed
    if (potentialNewValue !== currentValue.value[key]) {
      currentValue.value[key] = potentialNewValue;
      updateHasChanges();
    }
  }
}

  const handleDirectInput = (key, value) => {
    // Ensure value is not less than plan base limit
    const minAllowed = Math.max(usedLimits.value[key], minValue[key])
    // Apply minimum value constraint
    currentValue.value[key] = Math.max(value, minAllowed)
    updateHasChanges()
  }

  const calculateAdditionalCost = () => {
    let total = 0

    // Calculate for each add-on
    Object.keys(currentValue.value).forEach((key) => {
      // Skip workspaces and team members for agency plans
      if (isAgencyPlan.value && (key === 'workspaces' || key === 'teamMembers')) {
        return
      }

      const planLimit = planLimits.value[key] || minValue[key]
      const additionalUnits = currentValue.value[key] - planLimit

      if (additionalUnits > 0) {
        if (key === 'socialAccounts') {
          if (isAgencyPlan.value) {
            // For agency plans, use getExtraCharge with total accounts (plan + addon)
            const totalAccounts = currentValue.value[key]
            total += getExtraCharge(totalAccounts)
          } else {
            // For non-agency plans, charge $5 per extra account
            total += additionalUnits * prices[key]
          }
        } else if (key === 'aiTextCredits') {
          // AI Text Credits are priced per 1000 words
          total += getAiTextCreditsPrice(additionalUnits)
        } else if (key === 'aiImageCredits') {
          // AI Image Credits are priced per 100 credits
          total += (additionalUnits / 100) * prices[key]
        } else {
          total += additionalUnits * prices[key]
        }
      }
    })

    return total
  }

  const totalAdditionalCost = computed(() => {
    let total = calculateAdditionalCost()
    total = applyDiscount(total, planSlug.value, isAnnually.value)
    return formatPrice(total)
  })

  const updateHasChanges = () => {
    // Check if any value has changed from original
    hasChanges.value = false

    Object.keys(currentValue.value).forEach((key) => {
      const originalVal = originalValues.value[key] || minValue[key]
      if (currentValue.value[key] !== originalVal) {
        hasChanges.value = true
      }
    })
  }

  const calculateAddonsLimits = () => {
    // Calculate addon limits for API
    const addons = {}

    Object.keys(currentValue.value).forEach((key) => {
      // Skip workspaces and team members for agency plans
      if (isAgencyPlan.value && (key === 'workspaces' || key === 'teamMembers')) {
        return
      }
      const planLimit = planLimits.value[key] || minValue[key]
      const additionalUnits = currentValue.value[key] - planLimit
      addons[key] = additionalUnits
    })

    return addons
  }

  const fetchSubscriptionLimits = async (workspaceId) => {
    loading.value = true
    try {
      const response = await getSubscriptionLimits(workspaceId)

      if (response.status) {
        isAnnually.value = response.data?.is_annually || false
        isAgencyPlan.value = response.data?.is_agency_plan || false
        planSlug.value = response.data?.plan_slug || ''

        const { plan_limits: planLimitsData, addons_limits: addonsLimitsData, used_limits: usedLimitsData } = response.data

        // Map API response to our data structure
        planLimits.value = {
          workspaces: planLimitsData.workspaces || minValue.workspaces,
          socialAccounts: planLimitsData.profiles || minValue.socialAccounts,
          teamMembers: planLimitsData.members || minValue.teamMembers,
          aiImageCredits: planLimitsData.image_generation_credit || minValue.aiImageCredits,
          aiTextCredits: planLimitsData.caption_generation_credit || minValue.aiTextCredits,
          blogs: planLimitsData.blogs || minValue.blogs,
          mediaStorage: planLimitsData.media_storage ? Math.ceil(planLimitsData.media_storage / (1024 * 1024 * 1024)) : minValue.mediaStorage,
        }

        // Map addons limits
        if (addonsLimitsData && Object.keys(addonsLimitsData).length > 0) {
          addonsLimits.value = {
            workspaces: addonsLimitsData.workspaces || 0,
            socialAccounts: addonsLimitsData.profiles || 0,
            teamMembers: addonsLimitsData.members || 0,
            aiImageCredits: addonsLimitsData.image_generation_credit || 0,
            aiTextCredits: addonsLimitsData.caption_generation_credit || 0,
            blogs: addonsLimitsData.blogs || 0,
            mediaStorage: addonsLimitsData.media_storage || 0,
          }
        } else {
          addonsLimits.value = {
            workspaces: 0,
            socialAccounts: 0,
            teamMembers: 0,
            aiImageCredits: 0,
            aiTextCredits: 0,
            blogs: 0,
            mediaStorage: 0,
          }
        }

        // Map used limits
        usedLimits.value = {
          workspaces: usedLimitsData.workspaces || 0,
          socialAccounts: usedLimitsData.profiles || 0,
          teamMembers: usedLimitsData.members || 0,
          aiImageCredits: usedLimitsData.image_generation_credit || 0,
          aiTextCredits: usedLimitsData.caption_generation_credit || 0,
          blogs: usedLimitsData.blogs || 0,
          mediaStorage: usedLimitsData.media_storage ? Number((usedLimitsData.media_storage / (1024 * 1024 * 1024)).toFixed(2)) : 0,
        }

        // Calculate total available limits (plan + addons)
        const totalLimits = {}
        Object.keys(planLimits.value).forEach(key => {
          totalLimits[key] = planLimits.value[key] + (addonsLimits.value[key] || 0)
        })

        // Set current values to total limits
        currentValue.value = { ...totalLimits }

        // Save original values for change detection
        originalValues.value = { ...totalLimits }

        return true
      }
      return false
    } catch (error) {
      console.error('Error fetching subscription limits:', error)
      return false
    } finally {
      loading.value = false
    }
  }

  const handlePurchase = async (workspaceId, confirmCallback) => {
    isLoading.value = true

    try {
      // Prepare the add-ons data for purchase
      const addonsLimits = calculateAddonsLimits()

      // Call updateLimits API
      const response = await updateLimits(workspaceId, {
        addons: addonsLimits,
        preview: false
      })

      if(response?.status) {
        alertMessage('Limits updated successfully!', 'success')

        userMaven.track('addons_limits_updated')
        return true
      }

      alertMessage(response.message || 'Failed to update limits. Please contact support (support@contentstudio.io)', 'error')
      isLoading.value = false
      return false
    } catch (error) {
      alertMessage('An error occurred. Please contact support (support@contentstudio.io).', 'error')
      console.error('Error updating limits:', error)
      isLoading.value = false
      return false
    }
  }

  // Apply discount based on plan and billing cycle
  const applyDiscount = (amount, planSlug, isAnnually) => {
    if (isAnnually) {
      amount = amount * 12;
      switch (planSlug) {
        case 'standard-annually':
          return amount * (1 - 0.3448); // 34.48% discount
        case 'advanced-annually':
          return amount * (1 - 0.2899); // 28.99% discount
        case 'agency-unlimited-annually':
          return amount * (1 - 0.2878); // 28.78% discount
      }
    }
    return amount;
  }

  // Watch for changes to update hasChanges flag
  watch(currentValue, () => {
    updateHasChanges()
  })

  return {
    // State
    isAnnually,
    isAgencyPlan,
    hasChanges,
    loading,
    planLimits,
    addonsLimits,
    usedLimits,
    currentValue,
    maxValue,
    isLoading,
    limitItems,
    filteredLimitItems,
    totalAdditionalCost,
    addonAdjustments,
    planSlug,

    // Methods
    increaseValue,
    decreaseValue,
    handleDirectInput,
    calculateItemPrice,
    calculateAddonCount,
    fetchSubscriptionLimits,
    handlePurchase,
    formatNumber,
    formatMediaStorage,
    formatPrice
  }
}
